import * as React from "react"

const TOAST_LIMIT = 1
const TOAST_REMOVE_DELAY = 5000

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
}

let count = 0

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER
  return count.toString()
}

const toastTimeouts = new Map()

const addToRemoveQueue = (toastId) => {
  if (toastTimeouts.has(toastId)) {
    return
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId)

    dispatch({
      type: actionTypes.REMOVE_TOAST,
      toastId,
    })
  }, TOAST_REMOVE_DELAY)

  toastTimeouts.set(toastId, timeout)
}

export const reducer = (state, action) => {
  switch (action.type) {
    case actionTypes.ADD_TOAST:
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      }

    case actionTypes.UPDATE_TOAST:
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id
            ? { ...t, ...action.toast }
            : t
        ),
      }

    case actionTypes.DISMISS_TOAST: {
      const { toastId } = action

      if (toastId) {
        addToRemoveQueue(toastId)
      } else {
        state.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id)
        })
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t
        ),
      }
    }

    case actionTypes.REMOVE_TOAST:
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        }
      }

      return {
        ...state,
        toasts: state.toasts.filter(
          (t) => t.id !== action.toastId
        ),
      }

    default:
      return state
  }
}

const listeners = []

let memoryState = {
  toasts: [],
}

function dispatch(action) {
  memoryState = reducer(memoryState, action)

  listeners.forEach((listener) => {
    listener(memoryState)
  })
}

function toast({ ...props }) {
  const id = genId()

  let timer = null
  let startTime = Date.now()
  let remainingTime = TOAST_REMOVE_DELAY

  const startTimer = () => {
    if (timer || remainingTime <= 0) {
      return
    }

    startTime = Date.now()

    timer = setTimeout(() => {
      timer = null
      remainingTime = 0
      dismiss()
    }, remainingTime)
  }

  const pauseTimer = () => {
    if (!timer) {
      return
    }

    clearTimeout(timer)
    timer = null

    const elapsed = Date.now() - startTime

    remainingTime = Math.max(
      0,
      remainingTime - elapsed
    )
  }

  const dismiss = () => {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }

    dispatch({
      type: actionTypes.DISMISS_TOAST,
      toastId: id,
    })
  }

  const update = (props) =>
    dispatch({
      type: actionTypes.UPDATE_TOAST,
      toast: {
        ...props,
        id,
      },
    })

  dispatch({
    type: actionTypes.ADD_TOAST,
    toast: {
      ...props,
      id,
      open: true,

      onOpenChange: (open) => {
        if (!open) {
          dismiss()
        }
      },

      onMouseEnter: () => {
        pauseTimer()
      },

      onMouseLeave: () => {
        startTimer()
      },
    },
  })

  // Start the initial 5-second timer
  startTimer()

  return {
    id,

    dismiss,

    update,
  }
}

function useToast() {
  const [state, setState] = React.useState(memoryState)

  React.useEffect(() => {
    listeners.push(setState)

    return () => {
      const index = listeners.indexOf(setState)

      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }, [])

  return {
    ...state,

    toast,

    dismiss: (toastId) =>
      dispatch({
        type: actionTypes.DISMISS_TOAST,
        toastId,
      }),
  }
}

export { useToast, toast }